<template>
	<div class="relative min-h-screen flex items-start justify-center overflow-hidden bg-[#FFF2E0] pt-20 p-4">
		<!-- bg animation -->
		<div class="absolute inset-0 z-0 pointer-events-none">
			<div class="absolute inset-0 animated-gradient"></div>
			<div class="blob blob-1"></div>
			<div class="blob blob-2"></div>
		</div>

		<div class="relative z-10 bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden mt-20">

			<!-- header -->
			<div class="sticky top-0 bg-white px-8 pt-8 pb-4 border-b border-gray-200">
				<div class="flex flex-col items-center space-y-4">
					<img :src="(currentTheme === 'dark'|| currentTheme === 'navy') ? lightLogo : lightLogo" alt="Logo" class="w-32 md:w-40"/>
					<h1 class="text-2xl font-archivomedium text-gray-800">
						Cardware Enterprise
					</h1>
				</div>
				<div class="mt-6 flex justify-center space-x-6">
					<button @click="mode='login'" :class="toggleClass('login')">
						Login
					</button>
					<button @click="mode='signup'" :class="toggleClass('signup')">
						Sign Up
					</button>
				</div>
			</div>

			<!-- content -->
			<transition name="fade" mode="out-in">
				<div class="px-8 py-6" :key="mode">
					<form @submit.prevent="handleSubmit" novalidate class="space-y-5">
						<div>
							<label class="block text-sm text-gray-700">
								Email
							</label>
							<input v-model="form.email" @input="form.email = form.email.toLowerCase()" type="email" placeholder="you@example.com" class="w-full px-4 py-2 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#FC9984] transition"/>
						</div>
						<div class="relative">
							<label class="block text-sm text-gray-700">
								Password
							</label>
							<input v-model="form.password" :type="showPassword ? 'text' : 'password'" placeholder="••••••••" class="w-full px-4 py-2 pr-10 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#FC9984] transition"/>
							<button type="button" @click="showPassword = !showPassword" class="absolute right-3 top-1 transform -translate-y-1/2 flex items-center text-gray-500 hover:text-gray-700" :aria-label="showPassword ? 'Hide password' : 'Show password'">
								<!-- simple eye / eye-off icons (you can swap these for your SVGs) -->
								<svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
								</svg>
								<svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a10.05 10.05 0 012.112-3.504M9.878 9.88a3 3 0 104.244 4.242m1.415-1.414L21 21m-2.122-2.122L2.122 2.122" />
								</svg>
							</button>
						</div>
						<div v-if="mode==='signup'" class="relative">
							<label class="block text-sm text-gray-700">
								Confirm Password
							</label>
							<input v-model="form.confirmPassword" :type="showConfirmPassword ? 'text' : 'password'" placeholder="••••••••" class="w-full px-4 py-2 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#FC9984] transition"/>
							<button type="button" @click="showConfirmPassword = !showConfirmPassword" class="absolute right-3 top-1 transform -translate-y-1/2 flex items-center text-gray-500 hover:text-gray-700" :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'">
								<svg v-if="!showConfirmPassword" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
								</svg>
								<svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a10.05 10.05 0 012.112-3.504M9.878 9.88a3 3 0 104.244 4.242m1.415-1.414L21 21m-2.122-2.122L2.122 2.122" />
								</svg>
							</button>
						</div>
						<div v-if="mode==='signup'" class="flex items-center space-x-2">
							<input v-model="form.acceptedTerms" type="checkbox" id="terms" class="h-4 w-4 text-[#FC9984] focus:ring-[#FC9984] border-gray-300 rounded"/>
							<label for="terms" class="text-sm text-gray-700">
								I accept the <a href="https://docs.cardwarewallet.com/cardware-wallet-documentation/compliance-pack" target="_blank" class="text-[#FC9984] hover:underline">Terms & Conditions</a>
							</label>
						</div>
						<div class="flex items-center justify-between">
							<a v-if="mode==='login'" href="#" @click.prevent="handleForgot" class="text-sm text-[#FC9984] hover:underline">
								Forgot password?
							</a>
							<div></div>
							<button type="submit" :disabled="loading" class="px-6 py-2 rounded-full bg-[#FC9984] text-white font-archivomedium border border-[#FC9984] hover:bg-transparent hover:text-[#FC9984] transition
							disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
								<span v-if="!loading">{{ mode==='login' ? 'Log In' : 'Sign Up' }}</span>
								<div v-else class="loader w-5 h-5"></div>
							</button>
						</div>
					</form>
				</div>
			</transition>
		</div>
	</div>
</template>

<script setup>
	import { ref, reactive } from 'vue';
	import lightLogo from '../assets/CWWLogo.png';
	import darkLogo  from '../assets/LogoWhite1.png';

	const showPassword = ref(false);
	const showConfirmPassword = ref(false);

	const props = defineProps({
		currentTheme: { type: String, required: true },
		loading: { type: Boolean, default: false }
	});
	const emit = defineEmits([
		'login',
		'signup',
		'forgot-password',
		'validation-error'
	]);

	const mode = ref('login');
	const form = reactive({
		email: '',
		password: '',
		confirmPassword: '',
		acceptedTerms: false
	});

	const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/;

	function handleSubmit() {
		if (mode.value === 'login') {
			if (!form.email || !form.password) {
				emit('validation-error', 'Please enter both email & password.');
				return;
			}
			emit('login', { email: form.email, password: form.password });
			return;
		}

		if (!form.email || !form.password || !form.confirmPassword) {
			emit('validation-error', 'Please complete all fields.');
			return;
		}

		if (!passwordPattern.test(form.password)) {
			emit( 'validation-error', 'Password must be ≥8 chars, include upper+lower case, a number & a symbol.'
		);
		return;
		}

		if (form.password !== form.confirmPassword) {
			emit('validation-error', 'Passwords do not match.');
			return;
		}

		if (!form.acceptedTerms) {
			emit('validation-error', 'Please accept our Terms & Conditions.');
			return;
		}

		emit('signup', {
			email: form.email,
			password: form.password,
			confirmPassword: form.confirmPassword,
			acceptedTerms: form.acceptedTerms
		});
		form.email = '';
		form.password = '';
		form.confirmPassword = '';
		form.acceptedTerms = false;
	}

	function handleForgot() {
		if (!form.email) {
			emit('validation-error', 'Please enter your email to reset password.');
			return;
		}
			emit('forgot-password', form.email);
	}

	function toggleClass(val) {
		return ['px-4 pb-1 font-archivoregular transition', mode.value === val ? 'border-b-2 border-[#FC9984] text-[#FC9984]' : 'text-gray-400 hover:text-[#FC9984]'];
	}

</script>


<style scoped>

	.loader {
		border: 4px solid #f3f3f3;
		border-top: 4px solid #FC9984;
		border-radius: 50%;
		width: 1rem;
		height: 1rem;
		animation: spin 1s linear infinite;
	}
	@keyframes spin {
		to { transform: rotate(360deg); }
	}

	.fade-enter-active, .fade-leave-active {
		transition: opacity 0.25s ease;
	}
	.fade-enter-from, .fade-leave-to {
		opacity: 0;
	}

	.animated-gradient {
		position:absolute; inset:0;
		background: linear-gradient(60deg, #FFF2E0, #FFEFD9 ,#FFE0E2,);
		background-size: 300% 300%;
		animation: moveGrad 10s ease infinite;
		filter: blur(100px);
		opacity: 0.6;
	}

	.blob {
		position:absolute; border-radius:50%; filter: blur(80px); opacity:0.7;
	}
	.blob-1 {
		width:300px; height:300px; background:#FFCEB0;
		top:-80px; left:-60px;
		animation: float1 20s ease-in-out infinite;
	}
	.blob-2 {
		width:250px; height:250px; background:#FFEFD9;
		bottom:-60px; right:-40px;
		animation: float2 18s ease-in-out infinite;
	}

	@keyframes moveGrad {
		0%{background-position:0% 50%}
		50%{background-position:100% 50%}
		100%{background-position:0% 50%}
	}
	@keyframes float1 {
		0%,100%{transform:translate(0,0) scale(1)}
		50%{transform:translate(40px,-30px) scale(1.1)}
	}
	@keyframes float2 {
		0%,100%{transform:translate(0,0) scale(1)}
		50%{transform:translate(-30px,20px) scale(0.9)}
	}
	@keyframes drift1 {
		0%{transform:translate(0,0)}
		100%{transform:translate(-120px,120px)}
	}
	@keyframes drift2 {
		0%{transform:translate(0,0)}
		100%{transform:translate(100px,-100px)}
	}
</style>
